import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { BackupCodesNotice } from '@/features/auth/components/BackupCodesNotice';
import { useSelfTwoFactorRegenerateMutation } from '@/features/auth/hooks/useTwoFactorMutations';

interface TwoFactorRegenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 백업 코드 재발급 모달. 확인 시 regenerate 호출 → 새 코드 1회 노출. 기존 코드는 모두
 * 폐기된다. 로그인된 admin 의 self-service 흐름이므로 admin-access 세션 토큰으로
 * `/admin/v1/me/2fa/backup-codes/regenerate` 를 호출한다.
 */
export const TwoFactorRegenerateDialog = ({
  open,
  onOpenChange,
}: TwoFactorRegenerateDialogProps) => {
  const regenerate = useSelfTwoFactorRegenerateMutation();

  const handleOpenChange = (next: boolean) => {
    if (!next) regenerate.reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>백업 코드 재발급</DialogTitle>
          <DialogDescription>
            새 백업 코드를 발급하면 기존 코드는 모두 무효화됩니다. 발급된 코드는 이번 한 번만
            표시됩니다.
          </DialogDescription>
        </DialogHeader>

        {regenerate.isSuccess ? (
          <BackupCodesNotice codes={regenerate.data.backupCodes} />
        ) : regenerate.isError ? (
          <div
            className="rounded-md p-3 text-sm"
            role="alert"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            백업 코드 재발급에 실패했습니다. ({regenerate.error.message})
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              {regenerate.isSuccess ? '닫기' : '취소'}
            </Button>
          </DialogClose>
          {regenerate.isSuccess ? null : (
            <Button
              variant="primary"
              size="sm"
              disabled={regenerate.isPending}
              onClick={() => regenerate.mutate()}
            >
              {regenerate.isPending ? '발급 중…' : '재발급'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
